/* AdminLayout - Buitn Planten branded admin shell with settings in the user menu and responsive navigation.
   Updated: Removed beta/alpha version switching and the "Alpha" badge. */

import { useEffect, useState } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { LogOut, User, ChevronDown, Settings, Truck, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import AdminNavbar from './AdminNavbar';
import AdminMobileNav from './AdminMobileNav';
import { hasAccess, type AdminRole } from '@/lib/supabase';

export default function AdminLayout() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [adminRole, setAdminRole] = useState<string | null>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        navigate('/kh-admin/login');
        return;
      }

      const { data: adminData } = await supabase
        .from('admins')
        .select('id, email, role, created_at, updated_at, last_login')
        .eq('id', user.id)
        .maybeSingle();

      if (!adminData) {
        await supabase.auth.signOut();
        navigate('/kh-admin/login');
        return;
      }

      setAdminRole(adminData.role);
      setLoading(false);
    } catch (error) {
      console.error('Auth check error:', error);
      navigate('/kh-admin/login');
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/kh-admin/login');
  };

  if (loading) {
    return null;
  }

  const isSuperadmin = adminRole === 'superadmin';

  return (
    <div className="min-h-screen bg-[#fcfaf6] kh-admin-root">
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className="sticky top-0 z-50 border-b border-[#e7e1d6] bg-[#fcfaf6] text-[#1a1a1a]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center h-16 gap-4">
            <motion.a
              href="/kh-admin"
              whileHover={{ scale: 1.03 }}
              className="flex-shrink-0"
            >
              <img
                src="/assets/logos/buitnplanten-logo-green.webp"
                alt="Buitn Planten Admin"
                className="h-12 w-auto"
              />
            </motion.a>

            {adminRole && <AdminNavbar adminRole={adminRole} />}

            <div className="ml-auto flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="hidden items-center gap-1 text-[#5a6552] hover:text-[#1e2b22] md:flex"
                  >
                    <User className="h-4 w-4" />
                    <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel className="text-xs font-medium uppercase tracking-wider text-[#5a6552]">
                    Instellingen
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {hasAccess(adminRole as AdminRole, 'settings') && (
                    <DropdownMenuItem
                      onClick={() => navigate('/kh-admin/delivery-settings')}
                      className="cursor-pointer"
                    >
                      <Truck className="h-4 w-4 mr-2" />
                      Bezorging
                    </DropdownMenuItem>
                  )}
                  {isSuperadmin && (
                    <DropdownMenuItem
                      onClick={() => navigate('/kh-admin/admins')}
                      className="cursor-pointer"
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Beheerders
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="cursor-pointer text-red-500 focus:text-red-500"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Uitloggen
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {adminRole && (
                <AdminMobileNav adminRole={adminRole} onSignOut={handleSignOut} />
              )}
            </div>
          </div>
        </div>
      </motion.header>

      <main>
        <Outlet />
      </main>
    </div>
  );
}
